import { EXCLUSION_REASON_PARAMS } from "./exclusion-reason-filter";

/**
 * When a Not found lead, or an Unsure one whose employer could not be verified (PREC-4522), can
 * be searched again (PREC-4520), decided once for both apps.
 *
 * The lead list's Retry used to be offered on every "person" reason while the API's retry knew
 * only company rows, so on a person list the button did nothing and said it had started
 * (PREC-4524). Then the web offered it on more leads than the API would search (company-list leads
 * have no CSV person row). The web offers the button with {@link canRetryPersonSearch}; the API
 * selects the rows to reset with the same reasons and list types, joined through the lead's
 * `csvEnrichmentPersonId`.
 */

/**
 * The reasons a lead's Retry searches again, by the bucket the lead is in:
 * - Not found: a search that failed on our side, and the two "we searched and found nobody"
 *   reasons that read as one label (PREC-4519);
 * - Unsure: an employer that could not be verified because the profile could not be read
 *   (PREC-4522, Andreas's Q5: "Unsure, with its own reason, Retry offered"). Only that one: every
 *   other Unsure reason is a verdict about the person, which a new search would not change.
 */
const RETRY_SEARCH_REASONS_BY_BUCKET: Readonly<Record<string, readonly string[]>> = {
	NOT_FOUND: [
		EXCLUSION_REASON_PARAMS.processingError,
		EXCLUSION_REASON_PARAMS.person,
		EXCLUSION_REASON_PARAMS.contactInfo,
	],
	UNSURE: [EXCLUSION_REASON_PARAMS.employerUnverified],
};

/** The buckets a lead's Retry searches in. */
export const RETRY_SEARCH_BUCKETS: readonly string[] = Object.keys(RETRY_SEARCH_REASONS_BY_BUCKET);

/** The reasons a lead's Retry searches again in `inclusionStatus`'s bucket; none in any other. */
export const retrySearchReasonsFor = (inclusionStatus?: string | null): readonly string[] =>
	inclusionStatus &&
	Object.prototype.hasOwnProperty.call(RETRY_SEARCH_REASONS_BY_BUCKET, inclusionStatus)
		? RETRY_SEARCH_REASONS_BY_BUCKET[inclusionStatus]
		: [];

/** Every reason a lead's Retry searches again, in any bucket. */
export const RETRY_SEARCH_REASON_PARAMS: readonly string[] = [
	...new Set(RETRY_SEARCH_BUCKETS.flatMap((bucket) => retrySearchReasonsFor(bucket))),
];

/**
 * The list types whose people come from a CSV person source and are searched by the list's own
 * run: an upload list and a custom-event list. Re-running any other list re-runs its own, paid
 * source, so its leads are not searched again.
 */
export const PERSON_RETRY_LIST_SOURCE_TYPES: readonly string[] = [
	"CSV_ENRICHMENTS",
	"CUSTOM_EVENTS",
];

export interface PersonRetryCandidate {
	inclusionStatus?: string | null;
	/** The lead's CSV person row; company-list leads and hand-added leads have none. */
	csvEnrichmentPersonId?: number | null;
	listSourceType?: string | null;
	/** The reasons that explain the lead in its bucket. */
	reasonParams: readonly (string | null | undefined)[];
}

/** Whether the lead's Retry will search it again: a reason its bucket retries (PREC-4522). */
export const canRetryPersonSearch = (lead: PersonRetryCandidate): boolean => {
	const retried = retrySearchReasonsFor(lead.inclusionStatus);

	return (
		!!lead.csvEnrichmentPersonId &&
		PERSON_RETRY_LIST_SOURCE_TYPES.includes(lead.listSourceType ?? "") &&
		lead.reasonParams.some((param) => !!param && retried.includes(param))
	);
};

/**
 * Why a Retry queued nothing, so the button can say so instead of "started":
 * - `NOTHING_TO_RETRY`: the lead no longer carries a reason its bucket retries, or its source is gone;
 * - `ALREADY_SEARCHING`: the lead's row (or another row of the same person) is already waiting for,
 *   or in, a search;
 * - `LIST_INACTIVE`: the list is paused or stopped, so its run would not search anything;
 * - `RUN_IN_PROGRESS`: a run is going, and it may already be past the lead;
 * - `NOT_ENOUGH_CREDITS`: the run would stop before it searched;
 * - `LIST_AT_CAP`: the list has reached its lead limit, so its run would not get to the lead;
 * - `OTHERS_WAITING`: the run would also search `pendingOthers` other waiting people, each found
 *   one charged; the button asks first and sends `includeOthers` once the user agreed.
 */
export const RETRY_NOT_QUEUED_REASONS = [
	"NOTHING_TO_RETRY",
	"ALREADY_SEARCHING",
	"LIST_INACTIVE",
	"RUN_IN_PROGRESS",
	"NOT_ENOUGH_CREDITS",
	"LIST_AT_CAP",
	"OTHERS_WAITING",
] as const;

export type RetryNotQueuedReason = (typeof RETRY_NOT_QUEUED_REASONS)[number];
